/**
 * DemoEndSlate — the shared closing moment for all three demo reels
 * (/demo/story, /demo/admin-story, /demo/donor-story).
 *
 * Reuses the exact HeroBrandEntrance component that lives on the landing
 * hero — the "MY RICH AUNT" wordmark + necklace with the 5 palette gems.
 * The hero already carries:
 *   - the hairline sweep + wordmark clip-reveal + chain draw
 *   - the pearls landing on the chain (5-color palette)
 *   - the wordmark shimmer sweep (idle loop)
 *   - the gentle chain sway (idle loop)
 *   - 3D-lit polished-enamel gems
 *
 * We pass `autoPlay={true}` so the entrance animation fires the moment
 * Act 06 mounts (no scroll trigger needed inside the reel).
 *
 * Below the entrance sits the reel's Play again / Explore the platform
 * CTAs. That's the whole slate — no additional copy, no separate columns.
 * The wordmark IS the message.
 */

import { Link } from "react-router-dom";
import { RotateCcw, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { HeroBrandEntrance } from "./HeroBrandEntrance";

export const DemoEndSlate = ({ onReplay, className = "" }) => (
  <div
    data-testid="demo-endslate"
    className={`relative flex w-full flex-col items-center overflow-hidden text-ink ${className}`}
  >
    {/* Ceremonial warm-yellow glow — subtle spotlight halo behind the
        wordmark so the closing moment reads as an "arrival" without
        boxing the end slate off from the rest of the reel canvas. */}
    <span
      aria-hidden
      className="pointer-events-none absolute inset-0 -z-0"
      style={{
        background:
          "radial-gradient(ellipse at 50% 42%, rgba(253,208,6,0.22) 0%, rgba(253,161,32,0.08) 32%, rgba(255,251,240,0) 68%)",
      }}
    />
    {/* Warm blossom + tangerine ambient in the lower corners */}
    <span
      aria-hidden
      className="pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-blossom/30 blur-3xl"
    />
    <span
      aria-hidden
      className="pointer-events-none absolute -bottom-20 -right-16 h-64 w-64 rounded-full bg-tangerine/20 blur-3xl"
    />

    {/* Signature entrance — exactly what lives on the landing hero,
        but triggered on mount instead of on scroll. */}
    <div className="relative z-10 w-full">
      <HeroBrandEntrance autoPlay className="mt-0" />
    </div>

    {/* CTAs — appear after the entrance settles */}
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 7.8, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="relative z-10 mt-10 flex flex-wrap items-center justify-center gap-3"
    >
      <button
        type="button"
        onClick={onReplay}
        data-testid="demo-endslate-replay"
        className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-sand transition hover:bg-forest"
      >
        <RotateCcw className="h-4 w-4" strokeWidth={2.2} />
        Play again
      </button>
      <Link
        to="/demo"
        data-testid="demo-endslate-back-to-demo"
        className="inline-flex items-center gap-2 rounded-full border border-ink/25 bg-bone px-6 py-3 text-sm font-semibold text-ink transition hover:bg-beige"
      >
        Explore the platform
        <ArrowRight className="h-4 w-4" strokeWidth={2} />
      </Link>
    </motion.div>
  </div>
);
